/**
 * Include/exclude rule rows for a segment. Field to allowed ops mirror `moment_core/segments/rules.py`.
 */
import { useId, type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { Rule, RuleField, RuleOp, Rules } from '@/api/types';
import { PLATFORMS, RISKS, SENTIMENTS } from '@/api/types';
import { Button } from '@/components/arc/button/button';
import { ChipGroup } from '@/components/arc/chip-group/chip-group';
import { Input } from '@/components/arc/input/input';
import { NumberField } from '@/components/arc/number-field/number-field';
import { Select } from '@/components/arc/select/select';
import { Slider } from '@/components/arc/slider/slider';
import { TagInput } from '@/components/arc/tag-input/tag-input';
import { labelize } from '@/lib/format';
import { DEFAULT_SIMILARITY, SimilarityControl } from './SimilarityControl';
import styles from './RuleBuilder.module.css';

const ARRAY_FIELDS: RuleField[] = ['topics', 'emotions', 'iab_categories', 'objects', 'brand_safety.flags'];
const JSON_FIELDS: RuleField[] = ['brands', 'products', 'people'];
const SCALAR_FIELDS: RuleField[] = [
  'sentiment',
  'setting',
  'activity',
  'brand_safety.risk',
  'channel',
  'platform',
  'language',
];
const TEXT_FIELDS: RuleField[] = ['transcript', 'scene_summary'];
const ALL_FIELDS: RuleField[] = [
  ...ARRAY_FIELDS,
  ...JSON_FIELDS,
  ...SCALAR_FIELDS,
  ...TEXT_FIELDS,
  'semantic',
  'duration_sec',
];

const FIELD_LABEL: Partial<Record<RuleField, string>> = {
  iab_categories: 'IAB categories',
  'brand_safety.risk': 'Brand safety risk',
  'brand_safety.flags': 'Brand safety flags',
  scene_summary: 'Scene summary',
  semantic: 'Semantic match',
  duration_sec: 'Duration (seconds)',
};
const fieldLabel = (f: RuleField) => FIELD_LABEL[f] ?? labelize(f);

const OP_LABEL: Record<RuleOp, string> = {
  contains_any: 'contains any of',
  contains_all: 'contains all of',
  not_contains: 'does not contain',
  in: 'is one of',
  not_in: 'is not one of',
  eq: 'equals',
  neq: 'does not equal',
  text_match: 'matches text',
  similar_to: 'is similar to',
  gte: 'is at least',
  lte: 'is at most',
};

const LIST_OPS: RuleOp[] = ['in', 'not_in', 'contains_any', 'contains_all', 'not_contains'];
const isListOp = (op: RuleOp) => LIST_OPS.includes(op);

export function opsFor(field: RuleField): RuleOp[] {
  if (field === 'semantic') return ['similar_to'];
  if (field === 'duration_sec') return ['gte', 'lte', 'eq'];
  if (TEXT_FIELDS.includes(field)) return ['text_match'];
  if (ARRAY_FIELDS.includes(field) || JSON_FIELDS.includes(field))
    return ['contains_any', 'contains_all', 'not_contains'];
  return ['in', 'not_in', 'eq', 'neq'];
}

export function defaultRule(field: RuleField): Rule {
  const op = opsFor(field)[0] ?? 'contains_any';
  if (field === 'semantic') return { field, op, value: '', threshold: DEFAULT_SIMILARITY };
  if (field === 'duration_sec') return { field, op, value: 60 };
  if (TEXT_FIELDS.includes(field)) return { field, op, value: '' };
  return { field, op, value: [] };
}

function enumOptions(field: RuleField): string[] | null {
  if (field === 'sentiment') return SENTIMENTS;
  if (field === 'brand_safety.risk') return RISKS;
  if (field === 'platform') return PLATFORMS;
  return null;
}

const FIELD_OPTIONS = ALL_FIELDS.map((f) => ({ value: f, label: fieldLabel(f) }));

function RuleRow({
  rule,
  onChange,
  onRemove,
}: {
  rule: Rule;
  onChange: (r: Rule) => void;
  onRemove: () => void;
}) {
  const ops = opsFor(rule.field);
  const enums = enumOptions(rule.field);
  const listValue = Array.isArray(rule.value) ? rule.value : rule.value !== '' ? [String(rule.value)] : [];
  const isList = isListOp(rule.op);

  const setField = (f: string) => onChange(defaultRule(f as RuleField));
  const setOp = (next: string) => {
    const op = next as RuleOp;
    const value = isListOp(op) ? listValue : Array.isArray(rule.value) ? (rule.value[0] ?? '') : rule.value;
    onChange({ ...rule, op, value });
  };

  let editor: ReactNode;
  if (rule.field === 'semantic') {
    editor = (
      <div className={styles.stack}>
        <Input
          label="Describe the moment"
          value={String(rule.value)}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          placeholder="e.g. cozy morning at home with family"
        />
        <SimilarityControl
          label="Similarity"
          value={rule.threshold ?? DEFAULT_SIMILARITY}
          onValueChange={(v) => onChange({ ...rule, threshold: v })}
        />
      </div>
    );
  } else if (rule.field === 'duration_sec') {
    editor = (
      <NumberField
        label="Seconds"
        value={typeof rule.value === 'number' ? rule.value : Number(rule.value) || 0}
        onValueChange={(v) => onChange({ ...rule, value: v })}
        min={0}
        step={1}
        suffix=" s"
      />
    );
  } else if (TEXT_FIELDS.includes(rule.field)) {
    editor = (
      <Input
        label="Text to match"
        value={String(rule.value)}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
        placeholder="Full-text query"
      />
    );
  } else if (enums && isList) {
    editor = (
      <div className={styles.chips}>
        <span className={styles.chipLabel}>Values</span>
        <ChipGroup
          label="Values"
          multiple
          options={enums.map((v) => ({ value: v, label: labelize(v) }))}
          value={listValue}
          onValueChange={(v) => onChange({ ...rule, value: v })}
        />
      </div>
    );
  } else if (enums) {
    editor = (
      <Select
        label="Value"
        value={String(rule.value)}
        onValueChange={(v) => onChange({ ...rule, value: v })}
        placeholder="Select a value"
        options={enums.map((v) => ({ value: v, label: labelize(v) }))}
      />
    );
  } else if (isList) {
    editor = (
      <TagInput
        label="Values"
        value={listValue}
        onValueChange={(v) => onChange({ ...rule, value: v })}
        placeholder={rule.field === 'iab_categories' ? 'IAB8, IAB17-44' : 'Type a value and press Enter'}
      />
    );
  } else {
    editor = (
      <Input
        label="Value"
        value={String(rule.value)}
        onChange={(e) => onChange({ ...rule, value: e.target.value })}
      />
    );
  }

  return (
    <div className={styles.row}>
      <Select label="Field" value={rule.field} onValueChange={setField} options={FIELD_OPTIONS} />
      <Select
        label="Condition"
        value={rule.op}
        onValueChange={setOp}
        options={ops.map((o) => ({ value: o, label: OP_LABEL[o] }))}
      />
      <div className={styles.stack}>
        {editor}
        {JSON_FIELDS.includes(rule.field) && rule.op !== 'not_contains' ? (
          <Slider
            label="Minimum confidence for this rule"
            value={rule.min_confidence ?? 0.6}
            onValueChange={(v) => onChange({ ...rule, min_confidence: v })}
            min={0}
            max={1}
            step={0.05}
            format={(v) => v.toFixed(2)}
          />
        ) : null}
      </div>
      <div className={styles.remove}>
        <Button type="button" variant="ghost" size="sm" aria-label="Remove condition" onClick={onRemove}>
          <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function Group({
  title,
  hint,
  rules,
  onChange,
}: {
  title: string;
  hint: string;
  rules: Rule[];
  onChange: (r: Rule[]) => void;
}) {
  const headingId = useId();
  return (
    <section className={styles.group} aria-labelledby={headingId}>
      <div className={styles.groupHead}>
        <div>
          <h3 id={headingId} className={styles.groupTitle}>
            {title} <span className="text-muted">({rules.length})</span>
          </h3>
          <p className={styles.groupHint}>{hint}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rules, defaultRule('topics')])}
        >
          <Plus size={16} strokeWidth={1.75} aria-hidden="true" />
          Add condition
        </Button>
      </div>
      <div className={styles.rows}>
        {rules.length === 0 ? (
          <p className={styles.empty}>No conditions yet. Add one to start matching moments.</p>
        ) : null}
        {rules.map((r, i) => (
          <RuleRow
            key={i}
            rule={r}
            onChange={(nr) => onChange(rules.map((x, j) => (j === i ? nr : x)))}
            onRemove={() => onChange(rules.filter((_, j) => j !== i))}
          />
        ))}
      </div>
    </section>
  );
}

export function RuleBuilder({ value, onChange }: { value: Rules; onChange: (r: Rules) => void }) {
  return (
    <div className={styles.root}>
      <Group
        title="Include"
        hint="A moment joins the segment when it matches these conditions."
        rules={value.include}
        onChange={(include) => onChange({ ...value, include })}
      />
      <Group
        title="Exclude"
        hint="A moment is dropped when it matches any of these conditions."
        rules={value.exclude}
        onChange={(exclude) => onChange({ ...value, exclude })}
      />
    </div>
  );
}

/** Rules are valid when there is at least one row and every row has a usable value. */
export function rulesValid(r: Rules): boolean {
  const ok = (x: Rule) =>
    Array.isArray(x.value) ? x.value.length > 0 : x.value !== '' && x.value !== null && x.value !== undefined;
  return r.include.length + r.exclude.length > 0 && [...r.include, ...r.exclude].every(ok);
}
